// src/utils/cookieUtils.ts
// Centralized cookie utilities to replace ~15 duplicate document.cookie operations

export interface CookieOptions {
  expires?: Date | string;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  maxAge?: number; // in seconds
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split('; ');
  const cookie = cookies.find(row => row.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
}

/**
 * Set a cookie with options
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const {
    expires,
    path = '/',
    secure = true,
    sameSite = 'Strict',
    maxAge
  } = options;

  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (expires) {
    const expiryDate = expires instanceof Date ? expires.toUTCString() : expires;
    cookieString += `; expires=${expiryDate}`;
  }

  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  }

  cookieString += `; path=${path}`;

  if (secure) {
    cookieString += '; Secure';
  }

  cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string, path: string = '/'): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Get auth token from cookie
 */
export function getAuthToken(): string | null {
  return getCookie('authToken');
}

/**
 * Clear all auth-related cookies
 */
export function clearAuthCookies(): void {
  deleteCookie('authToken');
  // Note: refreshToken is httpOnly, backend clears it
}
